import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { LoadingText } from "@repo/ui/Buttons/loadingText";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { popoverStyle } from "@repo/ui/popover/style";
import { IconClock } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Popover } from "react-aria-components";

/**
 * Shape shared by every "published version" variant the popover can display.
 *
 * Individual features (voice agent config, playbook) provide their own richer type
 * but must at minimum expose these fields.
 */
export interface VersionHistoryEntry {
  id: string;
  published_at: string;
  /** True if this is the currently live version (filtered out of the list). */
  is_current: boolean;
}

export interface VersionHistoryPopoverProps<V extends VersionHistoryEntry> {
  versions: readonly V[] | undefined;
  isLoading: boolean;
  isRollingBack: boolean;
  isDisabled?: boolean;
  /** Label shown for each version row (e.g. agent identity name, playbook name). */
  getLabel: (version: V) => string;
  /**
   * Called when the user confirms restoring a version. Should perform the
   * rollback and throw/reject on failure.
   */
  onRollback: (version: V) => Promise<void>;
  /** Sentence fragment used in the confirm modal, e.g. "your current live configuration". */
  restoreTargetDescription: ReactNode;
}

export function formatPublishedAt(publishedAt: string): string {
  const dateTime = DateTime.fromISO(publishedAt);
  const now = DateTime.now();

  // If same day, show time
  if (dateTime.hasSame(now, "day")) {
    return `Today at ${dateTime.toFormat("h:mm a")}`;
  }

  // If yesterday
  if (dateTime.hasSame(now.minus({ days: 1 }), "day")) {
    return `Yesterday at ${dateTime.toFormat("h:mm a")}`;
  }

  // Otherwise show date and time
  return dateTime.toFormat("MMM d, yyyy 'at' h:mm a");
}

export function VersionHistoryPopover<V extends VersionHistoryEntry>({
  versions,
  isLoading,
  isRollingBack,
  isDisabled,
  getLabel,
  onRollback,
  restoreTargetDescription,
}: VersionHistoryPopoverProps<V>) {
  // State for confirmation modal
  const [versionToRestore, setVersionToRestore] = useState<V | null>(null);

  const handleRestoreClick = (version: V, close: () => void) => {
    close();
    setVersionToRestore(version);
  };

  const handleConfirmRestore = async () => {
    if (!versionToRestore) return;

    await onRollback(versionToRestore);
    setVersionToRestore(null);
  };

  // Filter out the current version from the list (can't rollback to current)
  const previousVersions = (versions ?? []).filter((v) => !v.is_current);

  return (
    <>
      <DialogTrigger>
        <IconButton
          variant={ButtonVariant.Secondary}
          size={Size.MEDIUM}
          icon={<IconClock className="m-1 h-5 w-5" />}
          isDisabled={isDisabled}
          accessibleLabel="Version history"
        />
        <Popover placement="bottom end" className={popoverStyle().popover()}>
          <Dialog className="w-80 p-4 outline-hidden">
            {({ close }) => (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Version history
                  </h3>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader style={LoaderStyle.SPINNER} />
                  </div>
                ) : previousVersions.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    No previous versions available.
                  </p>
                ) : (
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {previousVersions.map((version) => (
                      <VersionItem
                        key={version.id}
                        label={getLabel(version)}
                        publishedAt={version.published_at}
                        isRollingBack={isRollingBack}
                        onRollback={() => handleRestoreClick(version, close)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>

      <ConfirmationModal
        isOpen={versionToRestore !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setVersionToRestore(null);
        }}
        title="Restore version"
        confirmText="Restore"
        onConfirm={() => void handleConfirmRestore()}
      >
        {versionToRestore && (
          <>
            Are you sure you want to restore the version from{" "}
            <span className="font-medium">
              {formatPublishedAt(versionToRestore.published_at)}
            </span>
            ? This will replace {restoreTargetDescription}.
          </>
        )}
      </ConfirmationModal>
    </>
  );
}

interface VersionItemProps {
  label: string;
  publishedAt: string;
  isRollingBack: boolean;
  onRollback: () => void;
}

function VersionItem({
  label,
  publishedAt,
  isRollingBack,
  onRollback,
}: VersionItemProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border-light p-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground">{label}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {formatPublishedAt(publishedAt)}
        </p>
      </div>
      <Button
        variant={ButtonVariant.Secondary}
        size={Size.MEDIUM}
        onClick={onRollback}
        isLoading={isRollingBack}
        isDisabled={isRollingBack}
      >
        <LoadingText isLoading={isRollingBack} stages={["Restoring..."]}>
          Restore
        </LoadingText>
      </Button>
    </div>
  );
}
